import { useState, useEffect, useMemo } from "react";
import React from "react";
import { observer, inject } from "mobx-react";
import ProductView from "./ProductView";
import ProductModalView from "./ProductModalView";
import CrossSellView from "./CrossSellView";

const Product = inject('wooCommerceStore', 'studioStore')(observer(({ product, crossSellProducts, view, wooCommerceStore, studioStore, forceCard, isFavoriteView }) => {
  if (!product) return null;

  const [quantity, setQuantity] = useState(0);
  const [inCart, setInCart] = useState(0);
  const [selectedVariation, setSelectedVariation] = useState(() => {
    if (product.type === "variable") {
      const publishedVariations = product.variations.filter((v) => v.status === "publish");
      return publishedVariations.length === 1 ? publishedVariations[0] : null;
    }
    return null;
  });
  const [productWeightQuantity, setProductWeightQuantity] = useState(null);

  const productToAdd = selectedVariation || product;

  const crosssellProduct = useMemo(() => {
    return product.cross_sell_ids?.length && crossSellProducts
      ? crossSellProducts.find(p => p.id === product.cross_sell_ids[0]) || null
      : null;
  }, [product.cross_sell_ids, crossSellProducts]);

  const cart = wooCommerceStore.cart;
  const cartVersion = wooCommerceStore.cartVersion;

  useEffect(() => {
    const cartItem = cart.find((item) => item.id === productToAdd.id);
    if (cartItem) {
      setInCart(cartItem.quantity);
      setQuantity(cartItem.quantity);
    } else {
      setInCart(0);
      setQuantity(0);
    }
  }, [cart, productToAdd.id, cartVersion]);

  // Effect to handle initial variation selection and single variation products
  useEffect(() => {
    if (product.type === "variable") {
      const publishedVariations = product.variations.filter((v) => v.status === "publish");
      // Only set the variation if we don't have one selected or if there's exactly one variation
      if (!selectedVariation || publishedVariations.length === 1) {
        setSelectedVariation(publishedVariations[0]);
      }
    }
  }, [product.type, product.variations, selectedVariation]);

  useEffect(() => {
    if (selectedVariation?.weight_quantity_based !== productWeightQuantity) {
      setProductWeightQuantity(selectedVariation?.weight_quantity_based ?? null);
    }
  }, [selectedVariation, productWeightQuantity]);

  const handleClearFromCart = () => {
    wooCommerceStore.removeFromCart(productToAdd.id);
  };

  const handleAddToCart = () => {
    const maxQuantity = productToAdd.max_cart_quantity ? parseInt(productToAdd.max_cart_quantity) : null;
    if (maxQuantity !== null && quantity + 1 > maxQuantity) {
      wooCommerceStore.quantityError = `Maximum quantity allowed for this product is ${maxQuantity}`;
      setTimeout(() => {
        wooCommerceStore.quantityError = null;
      }, 3000);
      return;
    }

    if (selectedVariation) {
      productToAdd.parent_name = product.name;
      productToAdd.parent_images = product.images;
      productToAdd.categories = product.categories;
      if (!productToAdd?.type) {
        productToAdd.type = product.type;
      }
    }
    const success = wooCommerceStore.addToCart(productToAdd, 1);
    if (success) {
      setQuantity(prev => prev + 1);
      setInCart(prev => prev + 1);
    }
  };

  const handleRemoveFromCart = () => {
    if (quantity > 0) {
      wooCommerceStore.updateCartItemQuantity(productToAdd.id, quantity - 1);
      setQuantity(prev => prev - 1);
      setInCart(prev => prev - 1);
    } else {
      setInCart(0);
    }
  };

  const handleUpdateQuantity = (newQuantity) => {
    const maxQuantity = productToAdd.max_cart_quantity ? parseInt(productToAdd.max_cart_quantity) : null;
    if (maxQuantity !== null && newQuantity > maxQuantity) {
      wooCommerceStore.quantityError = `Maximum quantity allowed for this product is ${maxQuantity}`;
      setTimeout(() => {
        wooCommerceStore.quantityError = null;
      }, 3000);
      return;
    }

    const success = wooCommerceStore.updateCartItemQuantity(productToAdd.id, newQuantity);
    if (success) {
      setQuantity(newQuantity);
      setInCart(newQuantity);
    }
  };

  const handleVariationChange = (e) => {
    const variationId = parseInt(e.target.value, 10); // Convert to number
    const publishedVariations = product.variations.filter((v) => v.status === "publish");
    const variation = publishedVariations.find((v) => v.id === variationId);
    if (variation) {
      const { weight_quantity_based: productWeightQuantity } = variation;
      setProductWeightQuantity(productWeightQuantity);
      setSelectedVariation(variation);
    }
  };

  const img = useMemo(() => {
    return selectedVariation?.image?.src ? selectedVariation.image : (product?.images?.[0] ?? {});
  }, [selectedVariation, product?.images]);

  return view === 'modal' ? (
    <ProductModalView
      product={product}
      inCart={inCart}
      img={img}
      handleRemoveFromCart={handleRemoveFromCart}
      handleAddToCart={handleAddToCart}
      handleClearFromCart={handleClearFromCart}
      handleUpdateQuantity={handleUpdateQuantity}
      productWeightQuantity={productWeightQuantity}
      quantity={quantity}
      selectedVariation={selectedVariation}
      setSelectedVariation={setSelectedVariation}
      handleVariationChange={handleVariationChange}
    />
  ) : view === 'crosssell' ? (
    <CrossSellView
      product={product}
      inCart={inCart}
      img={img}
      handleClick={() => wooCommerceStore.showProductModal(product)}
      selectedVariation={selectedVariation}
      setSelectedVariation={setSelectedVariation}
      handleRemoveFromCart={handleRemoveFromCart}
      handleAddToCart={handleAddToCart}
      handleClearFromCart={handleClearFromCart}
      handleUpdateQuantity={handleUpdateQuantity}
      productWeightQuantity={productWeightQuantity}
      quantity={quantity}
      handleVariationChange={handleVariationChange}
      studioStore={studioStore}
    />
  ) : (
    <ProductView
      product={product}
      inCart={inCart}
      img={img}
      handleClick={() => wooCommerceStore.showProductModal(product)}
      selectedVariation={selectedVariation}
      setSelectedVariation={setSelectedVariation}
      handleRemoveFromCart={handleRemoveFromCart}
      handleAddToCart={handleAddToCart}
      handleClearFromCart={handleClearFromCart}
      handleUpdateQuantity={handleUpdateQuantity}
      productWeightQuantity={productWeightQuantity}
      quantity={quantity}
      handleVariationChange={handleVariationChange}
      crosssellProduct={crosssellProduct}
      studioStore={studioStore}
      forceCard={forceCard}
      isFavoriteView={isFavoriteView}
    />
  );
}));

export default React.memo(Product, (prevProps, nextProps) => {
  return (
    prevProps.product?.id === nextProps.product?.id &&
    prevProps.view === nextProps.view &&
    prevProps.forceCard === nextProps.forceCard &&
    prevProps.isFavoriteView === nextProps.isFavoriteView &&
    prevProps.crossSellProducts === nextProps.crossSellProducts
  );
});
